import React, { useState, useMemo, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ShoppingBag, Sparkles, Loader2, Gift } from "lucide-react";
import { api, formatINR } from "@/lib/api";
import { useGet } from "@/lib/hooks";
import { useCart } from "@/context/CartContext";
import { useSettings } from "@/context/SettingsContext";
import { useContent } from "@/lib/content";
import { SectionHeading } from "@/components/wp/SectionHeading";
import { Button } from "@/components/ui/button";
import { Slider } from "@/components/ui/slider";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";

export default function Builder() {
  const { data: products } = useGet("/products");
  const { data: content } = useGet("/content");
  const settings = useSettings();
  const copy = useContent();
  const { addItem } = useCart();
  const minOrder = Number(settings?.min_order_value ?? 449);
  const [budget, setBudget] = useState(699);
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState([]);
  const [preview, setPreview] = useState(null);
  const [genLoading, setGenLoading] = useState(false);

  const categories = ["all", ...(content?.categories || [])];
  const list = useMemo(() => (products || []).filter((p) => category === "all" || p.category === category), [products, category]);
  const total = selected.reduce((s, p) => s + p.price, 0);
  const completion = Math.min(100, (selected.length / 4) * 100);
  // The price control is a positive spend guide, never a limit. The visible range
  // starts around the minimum order value and extends to ~₹1,500, expanding upward
  // to a sensible rounded value when the parcel total goes higher.
  const sliderMin = Math.max(299, Math.floor(minOrder / 50) * 50);
  const sliderMax = Math.max(1500, Math.ceil(total / 500) * 500);
  useEffect(() => {
    // Gently accommodate the current parcel value so nothing ever reads as "over".
    if (total > budget) setBudget(Math.min(sliderMax, Math.ceil(total / 50) * 50));
  }, [total]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = (p) => {
    setSelected((prev) => prev.find((x) => x.id === p.id) ? prev.filter((x) => x.id !== p.id) : [...prev, p]);
    setPreview(null);
  };

  const addToCart = () => {
    if (selected.length === 0) return toast.error("Add a few accessories first 💝");
    addItem({ id: "custom-" + Date.now(), name: `Custom Parcel (${selected.length} items)`, price: total, image: selected[0].image, type: "custom" });
    toast.success("Your custom parcel is in the cart ✨");
    setSelected([]);
  };

  const genPreview = async () => {
    if (selected.length === 0) return toast.error("Pick some accessories to preview.");
    setGenLoading(true);
    try {
      const { data } = await api.post("/ai/preview", { items: selected.map((p) => p.id), description: selected.map((p) => p.name).join(", ") });
      setPreview(data.image);
      toast.success("Preview ready ✨");
    } catch { toast.error("We couldn't create that preview just now."); }
    finally { setGenLoading(false); }
  };

  return (
    <div className="wp-container py-10">
      <SectionHeading eyebrow="Build Your Own Parcel" title={copy.builder_title} subtitle={copy.builder_subtitle} />

      <div className="mt-8 grid gap-8 lg:grid-cols-[1fr_22rem]">
        {/* picker */}
        <div>
          <div className="rounded-wp-lg border border-border bg-card p-4 shadow-wp-soft">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold">Spend guide</label>
              <span className="price-num font-semibold text-wp-rose-strong">{formatINR(budget)}</span>
            </div>
            <Slider value={[budget]} min={sliderMin} max={sliderMax} step={50} onValueChange={(v) => setBudget(v[0])} className="mt-3" data-testid="builder-budget-slider" />
            <p className="mt-2 text-xs text-wp-ink-muted">{copy.builder_spend_guide_note}</p>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {categories.map((c) => (
              <button key={c} onClick={() => setCategory(c)} data-testid={`builder-cat-${c.replace(/\s+/g, "-").toLowerCase()}`}
                className={`wp-chip ${category === c ? "bg-wp-rose-strong text-white border-transparent" : "hover:bg-wp-blush-200"}`}>{c === "all" ? "All" : c}</button>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {list.map((p) => {
              const isSel = selected.find((x) => x.id === p.id);
              return (
                <button key={p.id} onClick={() => toggle(p)} data-testid={`builder-item-${p.id}`}
                  className={`group relative overflow-hidden rounded-wp-md border bg-card text-left shadow-wp-soft transition-[transform,box-shadow] hover:-translate-y-0.5 ${isSel ? "border-wp-rose-strong ring-2 ring-wp-rose/40" : "border-border"}`}>
                  <div className="relative aspect-square overflow-hidden bg-wp-cream-100">
                    <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110" />
                    {isSel && <span className="absolute right-2 top-2 grid h-7 w-7 place-items-center rounded-full bg-wp-rose-strong text-white"><Check className="h-4 w-4" /></span>}
                  </div>
                  <div className="p-2.5">
                    <p className="line-clamp-1 text-xs font-medium">{p.name}</p>
                    <span className="price-num text-xs font-semibold text-wp-rose-strong">{formatINR(p.price)}</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* live preview */}
        <div className="lg:sticky lg:top-20 lg:self-start">
          <div className="rounded-wp-lg border border-border bg-card p-5 shadow-wp-card">
            <h3 className="flex items-center gap-2 font-display text-xl"><Gift className="h-5 w-5 text-wp-rose-strong" /> Your Parcel</h3>
            <Progress value={completion} className="mt-3 h-2" />
            <p className="mt-2 text-xs text-wp-ink-muted">{selected.length === 0 ? "Add 3–4 accessories for a perfect parcel." : `${selected.length} item${selected.length > 1 ? "s" : ""} selected`}</p>

            <div className="mt-4 min-h-[120px] rounded-wp-md border border-dashed border-border bg-wp-blush-50 p-3">
              {preview ? (
                <img src={preview} alt="Parcel preview" className="w-full rounded-wp-sm object-cover" />
              ) : selected.length === 0 ? (
                <div className="flex h-28 items-center justify-center text-center text-sm text-wp-ink-muted">Your selected items will appear here 🎀</div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <AnimatePresence>
                    {selected.map((p) => (
                      <motion.img key={p.id} layout initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.8 }}
                        src={p.image} alt={p.name} className="aspect-square w-full rounded-wp-sm object-cover" />
                    ))}
                  </AnimatePresence>
                </div>
              )}
            </div>

            <div className="mt-4 flex items-center justify-between">
              <span className="text-sm text-wp-ink-muted">Total</span>
              <span className="price-num text-2xl font-semibold text-wp-rose-strong" data-testid="builder-live-price">{formatINR(total)}</span>
            </div>

            <Button onClick={addToCart} disabled={selected.length === 0} data-testid="builder-add-to-cart-button" className="mt-4 w-full rounded-xl bg-wp-rose-strong text-white hover:brightness-105">
              <ShoppingBag className="mr-2 h-4 w-4" /> Add Parcel to Cart
            </Button>
            <Button onClick={genPreview} disabled={genLoading} variant="outline" data-testid="builder-ai-preview-button" className="mt-2 w-full rounded-xl">
              {genLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4 text-wp-ocean" />}
              {genLoading ? "Wrapping…" : "See a styled preview"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
